import React, { createContext, useContext, useRef, useState } from "react";
import { driver, Driver } from "driver.js";
import "driver.js/dist/driver.css";
import axios, { AxiosResponse } from "axios";
import { axiosPost } from "./helper";
import { completeAppTour } from "./api";

// Extend the DriverStep type to include the group property
interface Step {
  id?: string;
  element: string;
  popover?: {
    title?: string;
    description?: string;
    position?: string;
  };
  group?: string;
}

interface TourContextType {
  startTour: (index?: number) => void;
  stopTour: () => void;
  setSteps: (steps: Step[]) => void;
  moveNext: () => void;
  movePrevious: () => void;
  moveTo: (index: number) => void;
  isLastStep: () => boolean;
  isFirstStep: () => boolean;
  getActiveIndex: () => number | null;
  getActiveElement: () => Element | null;
  hasCompletedTour: boolean;
  getDriverInstance: () => Driver | null;
  registerGroupHandlers: (
    group: string,
    open: () => Promise<void>,
    close: () => Promise<void>,
  ) => void;
  isTourEnabled: boolean;
}

// Default empty context
const TourContext = createContext<TourContextType>({
  startTour: () => {},
  stopTour: () => {},
  setSteps: () => {},
  moveNext: () => {},
  movePrevious: () => {},
  moveTo: () => {},
  isLastStep: () => false,
  isFirstStep: () => false,
  getActiveIndex: () => null,
  getActiveElement: () => null,
  hasCompletedTour: false,
  getDriverInstance: () => null,
  registerGroupHandlers: () => {},
  isTourEnabled: true
});

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const driverRef = useRef<Driver | null>(null);
  const [steps, setStepsState] = useState<Step[]>([]);
  const [hasCompletedTour, setHasCompletedTour] = useState(
    localStorage.getItem("isAppTourCompleted") === "true" || false
  );
  const currentGroupRef = useRef<string | null>(null);
  const isTourEnabled = process.env.REACT_APP_ENABLE_TOUR === "true";

  // Store open/close handlers for each group
  const groupHandlersRef = useRef<
    Record<string, { open: () => void; close: () => void }>
  >({});

  const registerGroupHandlers = (
    group: string,
    open: () => Promise<void>,
    close: () => Promise<void>
  ) => {
    groupHandlersRef.current[group] = { open, close };
  };

  const initDriver = () => {
    driverRef.current = driver({
      steps,
      animate: true,
      popoverClass: "driverjs-theme",
      onHighlighted: async (element, step: any) => {
        if (step.group) {
          const prevGroup = currentGroupRef.current;

          // Close previous group if different
          if (prevGroup && prevGroup !== step.group) {
            const prevHandlers = groupHandlersRef.current[prevGroup];
            if (prevHandlers && prevHandlers.close) {
              await prevHandlers.close(); // Make close() return a promise
            }
          }

          currentGroupRef.current = step.group;

          const handlers = groupHandlersRef.current[step.group];
          if (handlers && handlers.open) {
            await handlers.open(); // Make open() return a promise after menu is fully rendered
          }

          // Optional: delay a little extra to ensure layout is updated
          await new Promise((resolve) => setTimeout(resolve, 200));
          setTimeout(() => {
            driverRef.current?.refresh(); // Recalculate tooltip position
          }, 50);
        }
      },
      onDestroyed: handleTourCompletion,
    });

    (driverRef.current as any).on?.("destroy", handleTourCompletion);
  };

  const startTour = (index: number = 0) => {
    if (!steps.length) return;
    if (!isTourEnabled) return;
    initDriver();
    driverRef.current?.drive(index);
  };

  const stopTour = () => {
    driverRef.current?.destroy();
    driverRef.current = null;
    currentGroupRef.current = null;
  };

  const setSteps = (newSteps: Step[]) => {
    setStepsState(newSteps);
  };
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  const moveNext = async () => {
    driverRef.current?.moveNext();
  };
  const movePrevious = async () => {
    driverRef.current?.movePrevious();
  };
  const moveTo = (index: number) => driverRef.current?.moveTo(index);
  const isLastStep = () => driverRef.current?.isLastStep() ?? false;
  const isFirstStep = () => driverRef.current?.isFirstStep() ?? false;
  const getActiveIndex = () => driverRef.current?.getActiveIndex() ?? null;
  const getActiveElement = () => driverRef.current?.getActiveElement() ?? null;
  const getDriverInstance = () => driverRef.current;

  const handleTourCompletion = async () => {
    const prevGroup = currentGroupRef.current;
    if (prevGroup) {
      const prevHandlers = groupHandlersRef.current[prevGroup];
      prevHandlers.close();
    }
    if (!hasCompletedTour) {
      try {
        const response = (await axiosPost(
          completeAppTour,
          {}
        )) as AxiosResponse;
        if (response.status === 200) {
          setHasCompletedTour(true);
          // console.log('Tour completion recorded successfully.');
        } else {
          setHasCompletedTour(false);
          // console.error('Failed to record tour completion:', response.statusText);
        }
        setHasCompletedTour(true);
      } catch (err) {
        console.error("Failed to complete tour:", err);
      }
    }
  };

  const contextValue: TourContextType = React.useMemo(
    () => ({
      startTour,
      stopTour,
      setSteps,
      moveNext,
      movePrevious,
      moveTo,
      isLastStep,
      isFirstStep,
      getActiveIndex,
      getActiveElement,
      hasCompletedTour,
      getDriverInstance,
      registerGroupHandlers,
      isTourEnabled,
    }),
    [
      startTour,
      stopTour,
      setSteps,
      moveNext,
      movePrevious,
      moveTo,
      isLastStep,
      isFirstStep,
      getActiveIndex,
      getActiveElement,
      hasCompletedTour,
      getDriverInstance,
      registerGroupHandlers,
      isTourEnabled
    ],
  );

  return (
    <TourContext.Provider value={contextValue}>{children}</TourContext.Provider>
  );
};

export const useTour = () => useContext(TourContext);
